/* eslint-disable max-len */
import { useState } from "react";
import { muiColors } from "themes/colors";
import { useQuery } from "@apollo/client";
import * as User from "providers/user";
import * as Axios from "providers/axios";
import * as Cognito from "providers/cognito";
// import { CustomButton } from "./CustomButton";
import { Box, Dialog, DialogContent, IconButton } from "@mui/material";
// import CloseIcon from "@mui/icons-material/Close";
import { CustomButton } from "components/CustomButton";
import CheckIcon from "@mui/icons-material/Check";
import { ArrowLeftIcon, LogoutIcon, TranslateIcon } from "assets/Icons";
import {
  Labeled,
  SimpleForm,
  useLocaleState,
  useNotify,
  useTranslate
} from "react-admin";
import { CardComponent } from "components/UserEdit";
import ShowCards from "components/ShowCards";
import { GET_COMPANY_PAYMENT_METHOD } from "gql";
import { stripePromise } from "stripeConfig";
import {
  CardElement,
  Elements,
  useElements,
  useStripe
} from "@stripe/react-stripe-js";
import GooglePlacesAutocomplete from "components/GooglePlacesAutocomplete";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import CloseIcon from "@mui/icons-material/Close";
import ConfirmationModal from "components/ConfirmationModal";
import logo from "assets/Logo.svg";

interface ChoosePlanModalProps {
  onClose?: () => void;
  open: boolean;
  alreadySelectedPlan?: string | null;
  hasSubscription?: boolean;
  currentPeriodEndAt?: any;
  cancelAtPeriodEndAt?: any;
  hasAnyFutureBooking?: boolean;
}

const changePlanWarningMessages = {
  "BASIC-FULL-WITHOUT": "companies.basic_to_full_without_appointments",
  "BASIC-LIMITED-WITHOUT": "companies.basic_to_limited_without_appointments",
  "LIMITED-FULL-WITH": "companies.limited_to_full_with_appointments",
  "LIMITED-FULL-WITHOUT": "companies.limited_to_full_without_appointments",
  "LIMITED-BASIC-WITH": "companies.limited_to_basic_with_appointments",
  "LIMITED-BASIC-WITHOUT": "companies.limited_to_basic_without_appointments",
  "FULL-LIMITED-WITH": "companies.full_to_limited_with_appointments",
  "FULL-LIMITED-WITHOUT": "companies.full_to_limited_without_appointments",
  "FULL-BASIC-WITH": "companies.full_to_basic_with_appointments",
  "FULL-BASIC-WITHOUT": "companies.full_to_basic_without_appointments"
};

const selectPlanCcMessages = {
  BASIC: "companies.select_basic_plan_cc_info",
  LIMITED: "companies.select_limited_plan_cc_info",
  FULL: "companies.select_full_plan_cc_info"
};

export default function ChoosePlanModalContainer({
  onClose,
  open,
  alreadySelectedPlan,
  hasSubscription,
  currentPeriodEndAt,
  cancelAtPeriodEndAt,
  hasAnyFutureBooking
}: ChoosePlanModalProps) {
  return (
    <Elements stripe={stripePromise}>
      <ChoosePlanModal
        onClose={onClose}
        open={open}
        alreadySelectedPlan={alreadySelectedPlan}
        hasSubscription={hasSubscription}
        currentPeriodEndAt={currentPeriodEndAt}
        cancelAtPeriodEndAt={cancelAtPeriodEndAt}
        hasAnyFutureBooking={hasAnyFutureBooking}
      />
    </Elements>
  );
}

function ChoosePlanModal({
  onClose,
  open,
  alreadySelectedPlan,
  hasSubscription,
  currentPeriodEndAt,
  cancelAtPeriodEndAt,
  hasAnyFutureBooking
}: ChoosePlanModalProps) {
  const translate = useTranslate();
  const { signOut } = Cognito.useCognito();
  const [locale, setLocale] = useLocaleState();
  const { permissions } = User.useUser();
  const { api } = Axios.useAxios();
  const notify = useNotify();
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [selectedPlanToConfirm, setSelectedPlanToConfirm] = useState<
    string | null
  >(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isNewCardEmpty, setIsNewCardEmpty] = useState<boolean>(true);
  const [stripeErrorMsg, setStripeErrorMsg] = useState<string>("");
  const [selectedDefaultCardIdState, setSelectedDefaultCardIdState] =
    useState<string>("");

  const { data: companyData, refetch } = useQuery(GET_COMPANY_PAYMENT_METHOD, {
    variables: {
      where: { id: permissions?.companyId }
    }
  });

  const plans = [
    {
      id: "BASIC",
      name: "BASIC",
      buttonName: "companies.select_basic_plan",
      price: (
        <span className="font-['OptimaLTStd'] text-[32px] text-[rgba(48,47,46,0.95)] font-[600]">
          {translate("companies.free")}
        </span>
      ),
      helperPriceText: "",
      color: "rgba(184, 114, 42, 0.3)",
      gradientHelper: "rgba(184, 114, 42, 0.1)",
      features: ["companies.salon_profile_page_and_menu"]
    },
    {
      id: "LIMITED",
      name: "LIMITED",
      buttonName: "companies.select_limited_plan",
      price: (
        <span className="font-['OptimaLTStd'] text-[32px] text-[rgba(48,47,46,0.95)] font-[600]">
          10<span className="text-[28px]">%</span>
        </span>
      ),
      helperPriceText: "companies.fee",
      color: "rgba(184, 114, 42, 0.5)",
      gradientHelper: "rgba(184, 114, 42, 0.2)",
      features: [
        "companies.salon_profile_page_and_menu",
        "companies.customer_booking"
      ]
    },
    {
      id: "FULL",
      name: "FULL",
      buttonName:
        !alreadySelectedPlan || !companyData?.company?.upgrade_trial_granted_at
          ? "companies.start_free_trial"
          : "companies.select_full_plan",
      price: (
        <span className="font-['OptimaLTStd'] text-[32px] text-[rgba(48,47,46,0.95)] font-[600]">
          <span className="text-[28px]">$</span>29.99
        </span>
      ),
      helperPriceText: "companies.slash_month",
      color: "rgba(184, 114, 42, 0.7)",
      gradientHelper: "rgba(184, 114, 42, 0.4)",
      features: [
        "companies.salon_profile_page_and_menu",
        "companies.customer_booking",
        "companies.admin_calendar",
        "companies.staffing",
        "companies.payment_processing",
        "companies.inventory_management",
        "companies.customer_data"
      ]
    }
  ];

  const submit = async (data: any) => {
    setIsLoading(true);
    const cards = companyData?.company?.StripePaymentMethods ?? [];
    const currentDefaultCard = cards.find((c: any) => c.is_default);

    try {
      if (alreadySelectedPlan) {
        await api.post("/v1/companies/change-plan", {
          company_id: permissions?.companyId,
          status: selectedPlanToConfirm
        });
        window.location.reload();
        return;
      }

      if (selectedPlan === "BASIC") {
        await api.post("/v1/companies/choose-plan", {
          company_id: permissions?.companyId,
          status: selectedPlan
        });

        window.location.reload();
        return;
      }

      if (
        selectedDefaultCardIdState &&
        selectedDefaultCardIdState !== currentDefaultCard?.id
      ) {
        await api.put("/v1/companies/billing-info", {
          company_id: permissions?.companyId,
          default_payment_method_id: selectedDefaultCardIdState,
          billing_info: {
            address: data.address,
            address_display: data.address_display,
            city: data.city,
            state: data.state,
            zipcode: data.zipcode,
            country: data.country,
            address_latitude: data.address_latitude,
            address_longitude: data.address_longitude,
            google_places_id: data.google_places_id
          }
        });
        await refetch();
      }

      if (!currentDefaultCard && isNewCardEmpty) {
        const message = "Payment method is required.";
        setStripeErrorMsg(message);
        notify(message, { type: "error" });
        return;
      }

      if (!stripe || !elements) {
        const message = translate("common.error");
        notify(message, { type: "error" });
        setStripeErrorMsg(message);
        return;
      }

      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        const message = translate("common.error");
        notify(message, { type: "error" });
        setStripeErrorMsg(message);
        return;
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: companyData?.company?.name,
          email: companyData?.company?.owner?.email,
          phone: companyData?.company?.owner?.phone_number || ""
        }
      });

      if (error) {
        const message = error?.message || translate("common.error");
        notify(message, { type: "error" });
        setStripeErrorMsg(message);
        return;
      }

      await api.post("/v1/companies/choose-plan", {
        payment_method: {
          id: paymentMethod?.id,
          brand: paymentMethod?.card?.brand,
          exp_month: paymentMethod?.card?.exp_month,
          exp_year: paymentMethod?.card?.exp_year,
          last4: paymentMethod?.card?.last4
        },
        company_id: permissions?.companyId,
        status: selectedPlan,
        billing_info: {
          address: data.address,
          address_display: data.address_display,
          city: data.city,
          state: data.state,
          zipcode: data.zipcode,
          country: data.country,
          address_latitude: data.address_latitude,
          address_longitude: data.address_longitude,
          google_places_id: data.google_places_id
        }
      });

      window.location.reload();
    } catch (e) {
      const message =
        e?.response?.data?.message || e?.message || translate("common.error");
      notify(message, { type: "error" });
      setStripeErrorMsg(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      title={""}
      open={Boolean(open)}
      fullScreen
      onClose={onClose}
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: muiColors.paper
          // borderRadius: "8px"
        },
        "& #edit-dialog-title": {
          textAlign: "center",
          textTransform: "uppercase",
          fontFamily: "Inter",
          fontSize: "14px"
        },
        "& .RaFormDialogTitle-closeButton": {
          display: "none"
        }
      }}
    >
      {/* <DialogTitle>
        <IconButton
          sx={{
            position: "absolute",
            top: "8px",
            right: "8px"
          }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle> */}
      {/* bg-[rgba(217,236,241,1)] */}
      <DialogContent
        sx={{
          backgroundColor: "rgba(217, 236, 241, 1)",
          fontFamily: "Inter",
          display: "flex",
          alignItems: "safe center"
        }}
      >
        {/* <div className="w-full h-full flex items-center justify-center bg-[rgba(217,236,241,1)]"> */}
        {onClose && (
          <IconButton
            className="!hidden min-[968px]:!flex"
            sx={{
              position: "absolute",
              top: "8px",
              right: "8px",
              zIndex: 10
            }}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        )}
        <div className="w-full h-full min-[968px]:h-auto">
          {!selectedPlan ? (
            <div className="w-full h-[1200px] flex flex-col items-start min-[550px]:items-center pb-6 pt-6 min-[968px]:h-full">
              {/* <div className="w-[250px] min-[410px]:w-[270px] sm:w-[360px] md:w-[464px] font-['Poppins'] relative text-[28px] min-[410px]:text-[30px] sm:text-[38px] md:text-[52px] text-[rgba(48,47,46,0.95)] font-[600] text-left sm:text-center">
                {translate("companies.pointment_pricing")}
                <img
                src={
                  locale === "en" ? oneMonthFreeImageEn : oneMonthFreeImageVi
                  }
                  className="absolute top-[-22px] right-[-140px] w-[125px] pointer-events-none"
                  alt="One Month Free"
                  />
                  </div> */}
              <div className="w-full flex justify-center">
                <div className="w-[220px] mb-4">
                  <img className="w-[220px]" src={logo} alt="logo" />
                </div>
              </div>
              <div className="max-w-[670px] px-0 sm:px-6 text-[14px] text-mono-primaryTextColor sm:text-[16px] md:text-[20px] text-center mt-[-3px] mb-6">
                {/* {translate("companies.get_a_software_system")} */}
                {!!alreadySelectedPlan
                  ? translate(
                      "companies.choose_the_pointment_subscription_plan_edit"
                    )
                  : translate(
                      "companies.choose_the_pointment_subscription_plan"
                    )}
              </div>
              {!onClose && (
                <div className="flex items-center gap-1 fixed top-2 left-2">
                  <TranslateIcon className="mr-[6px]" />
                  <span
                    className={`text-[12px] font-normal font-['Inter'] whitespace-nowrap cursor-pointer ${
                      locale === "en" ? "" : "hover:!text-mono-black"
                    }`}
                    style={{
                      color:
                        locale === "en" ? "#B8722A" : "rgba(48, 47, 46, 0.6)"
                    }}
                    onClick={() => setLocale("en")}
                    aria-hidden
                  >
                    English
                  </span>
                  <span
                    className="pointer-events-none text-[12px] font-normal font-['Inter'] whitespace-nowrap group-hover:!text-mono-black"
                    style={{ color: "rgba(48, 47, 46, 0.6)" }}
                  >
                    /
                  </span>
                  <span
                    className={`text-[12px] font-normal font-['Inter'] whitespace-nowrap cursor-pointer ${
                      locale === "vi"
                        ? ""
                        : "text-mono-750 hover:!text-mono-black"
                    }`}
                    style={{
                      color:
                        locale === "vi" ? "#B8722A" : "rgba(48, 47, 46, 0.6)"
                    }}
                    onClick={() => setLocale("vi")}
                    aria-hidden
                  >
                    {translate("navigation.Vietnamese")}
                  </span>
                </div>
              )}
              {!onClose && (
                <div
                  className={`fixed top-2 right-1 flex w-[90px] items-center gap-[14px] group cursor-pointer`}
                  onClick={() => {
                    signOut();
                  }}
                  aria-hidden
                >
                  <LogoutIcon />
                  <span
                    className="text-[12px] font-normal font-['Inter'] whitespace-nowrap group-hover:!text-mono-black"
                    style={{ color: "rgba(48, 47, 46, 0.6)" }}
                  >
                    {translate("navigation.log_out")}
                  </span>
                </div>
              )}
              <div className="flex justify-start w-full h-full items-center gap-4 flex-col min-[968px]:flex-row min-[968px]:justify-center">
                {plans?.map((plan) => (
                  <div className="flex flex-col min-[968px]:h-[435px] max-w-[500px] w-full bg-mono-white rounded-lg min-[968px]:w-[300px]">
                    <div
                      className="relative h-[60px] w-full p-4 bg-[rgba(184,114,42,0.2)] rounded-t-lg font-['OptimaLTStd'] text-[24px] text-[rgba(48,47,46,0.95)] font-[600] leading-8"
                      style={{
                        // backgroundColor: plan.color,
                        background: `linear-gradient(90deg, ${plan.gradientHelper} 0%, ${plan.color} 100%)`
                        // borderTop: `1px solid ${plan.color}`,
                        // borderLeft: `1px solid ${plan.color}`,
                        // borderRight: `1px solid ${plan.color}`
                      }}
                    >
                      {plan.name}
                      {/* {plan.id === "FULL" && (
                        <div
                          className="absolute top-[0px] right-0 bg-[#F6F4F1] border border-mono-primary text-[18px]
                         text-mono-primary px-3 py-2 leading-[18px] rounded-tr-lg font-['OptimaLTStd']"
                        >
                          Free for 90 days
                        </div>
                      )} */}
                      {!companyData?.company?.upgrade_trial_granted_at &&
                        plan.id === "FULL" && (
                          <div className="absolute top-[-30px] right-[-20px] w-[140px] h-[130px] pointer-events-none">
                            <svg
                              viewBox="0 0 190 190"
                              className="w-full h-full drop-shadow-sm"
                            >
                              <polygon
                                points="100,4 112,40 148,12 134,52 178,38 158,72 198,80 162,96 186,132 148,120 150,160 118,136 100,172 82,136 50,160 52,120 14,132 38,96 2,80 42,72 22,38 66,52 52,12 88,40"
                                fill="#F6F4F1"
                                stroke="rgba(48,47,46,0.85)"
                                strokeWidth="2"
                                strokeLinejoin="round"
                              />
                              <text
                                x="100"
                                y="76"
                                textAnchor="middle"
                                fill="rgba(48,47,46,0.95)"
                                fontSize="24"
                                fontWeight="700"
                                fontFamily="OptimaLTStd, sans-serif"
                              >
                                {translate("companies.free_for")}
                              </text>
                              <text
                                x="100"
                                y="104"
                                textAnchor="middle"
                                fill="rgba(48,47,46,0.95)"
                                fontSize="28"
                                fontWeight="800"
                                fontFamily="OptimaLTStd, sans-serif"
                              >
                                {translate("companies.ninety_days")}
                              </text>
                            </svg>
                          </div>
                        )}
                    </div>
                    <div
                      className="w-full h-full p-4 rounded-b-lg"
                      style={{
                        borderBottom: "1px solid #ebebeb",
                        borderLeft: "1px solid #ebebeb",
                        borderRight: "1px solid #ebebeb"
                      }}
                    >
                      <div className="mb-2">
                        <span className="font-['OptimaLTStd'] text-[32px] text-[rgba(48,47,46,0.95)] font-[600]">
                          {plan.price}
                        </span>
                        <span className="ml-1 text-[18px] text-[rgba(48,47,46,0.8)]">
                          {translate(plan.helperPriceText)}
                        </span>
                      </div>
                      <CustomButton
                        variant="secondary"
                        text={translate(plan.buttonName)}
                        onClick={() => {
                          if (alreadySelectedPlan) {
                            setSelectedPlanToConfirm(plan.id);
                          } else {
                            setSelectedPlan(plan.id);
                          }
                        }}
                        // sx={{ width: "100%", marginBottom: "20px" }}
                        disabled={alreadySelectedPlan === plan.id}
                        startIcon={
                          alreadySelectedPlan === plan.id ? (
                            <CheckIcon
                              sx={{
                                fontSize: "22px",
                                color: "rgba(184, 114, 42, 1)"
                              }}
                            />
                          ) : undefined
                        }
                        sx={{
                          width: "100%",
                          marginBottom: "20px",
                          padding: "0px 16px",
                          fontSize: "15px",
                          backgroundColor: "rgba(184, 114, 42, 0.05)",
                          color: "rgba(184, 114, 42, 1)",
                          borderColor: "rgba(184, 114, 42, 1)",
                          "&:hover": {
                            backgroundColor: "rgba(184, 114, 42, 0.1)"
                          },
                          "&:disabled": {
                            borderColor: "rgba(184, 114, 42, 0.1)",
                            backgroundColor: "rgba(184, 114, 42, 0.05)",
                            color: "rgba(184, 114, 42, 0.5)",
                            svg: {
                              fill: "rgba(184, 114, 42, 1)",
                              color: "rgba(184, 114, 42, 1)",
                              fillOpacity: 1,
                              path: {
                                fill: "rgba(184, 114, 42, 1)",
                                color: "rgba(184, 114, 42, 1)",
                                fillOpacity: 1
                              }
                            }
                          }
                        }}
                      />
                      {plan?.features?.map((feature) => (
                        <div className="flex items-center gap-2 mb-2 text-[16px] font-[300]">
                          <CheckIcon
                            sx={{
                              fontSize: "22px",
                              color: "rgba(184, 114, 42, 1)"
                            }}
                          />{" "}
                          {translate(feature)}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center p-2">
              <div className="w-full max-w-[500px] flex flex-col gap-4 bg-mono-white rounded-lg p-0 sm:p-2">
                <div className="flex items-center gap-[16px] ml-4 mt-4">
                  <IconButton
                    onClick={() => setSelectedPlan(null)}
                    sx={{
                      backgroundColor: "#FAF9F7",
                      border: "solid 1px rgba(48, 47, 46, 0.23)"
                    }}
                  >
                    <ArrowLeftIcon />
                  </IconButton>
                  <h4 className="font-['OptimaLTStd'] font-[600] mt-1.5">
                    {translate("companies.change_a_plan")}
                  </h4>
                </div>
                <Box
                  className="w-full flex flex-col"
                  sx={{
                    "& form": {
                      width: "100%",
                      "& > div": { paddingBottom: "16px" }
                    }
                  }}
                >
                  <div className="text-[28px] leading-10 text-[rgba(48,47,46,0.95)] font-[400] text-left mx-4">
                    {translate("companies.selected_plan")}
                    {": "}
                    <span
                      className="text-[rgba(184,114,42,1)] text-[42px] font-[600] font-['Famous']"
                      style={{ letterSpacing: "4px" }}
                    >
                      {selectedPlan}
                    </span>
                  </div>
                  {selectedPlan !== "BASIC" && (
                    <SimpleForm
                      onSubmit={submit}
                      resolver={yupResolver(validationSchema)}
                      toolbar={<div className="!pb-0" />}
                      className="w-full"
                    >
                      <div className="text-[24px] mb-3 text-[rgba(48,47,46,0.95)] font-[500]">
                        {translate("companies.billing_information")}:
                      </div>
                      <Labeled
                        paddingBottom={2}
                        label={translate("companies.payment_method")}
                        fullWidth
                      >
                        <>
                          <CardComponent
                            setIsNewCardEmpty={setIsNewCardEmpty}
                            setStripeErrorMsg={setStripeErrorMsg}
                          />
                          {stripeErrorMsg && (
                            <span className="text-error text-[12px]">
                              {stripeErrorMsg}
                            </span>
                          )}
                          <ShowCards
                            company={companyData?.company}
                            selectedDefaultCardIdState={
                              selectedDefaultCardIdState
                            }
                            setSelectedDefaultCardIdState={
                              setSelectedDefaultCardIdState
                            }
                            refetch={refetch}
                            editMode
                          />
                        </>
                      </Labeled>
                      {permissions?.isCompanyWithoutStatus && (
                        <div className="mb-2">
                          {translate(
                            selectPlanCcMessages[
                              selectedPlan as keyof typeof selectPlanCcMessages
                            ]
                          )}
                        </div>
                      )}
                      <Box
                        sx={{
                          width: "100%",
                          "& .MuiFilledInput-root": {
                            borderRadius: "8px",
                            border: "1px solid rgba(48, 47, 46, 0.06)",
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                            paddingBottom: "5px !important",
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.06)"
                            },
                            "& fieldset": {
                              border: "solid 1px transparent"
                            },
                            "&:hover fieldset": {
                              border: "solid 1px transparent"
                            },
                            "&.Mui-focused fieldset": {
                              border: "solid 1px #b8722a"
                            }
                          },
                          "& .MuiFilledInput-input": {
                            height: "18px",
                            fontSize: "16px",
                            borderRadius: "8px",
                            fontFamily: "Inter",
                            border: "none"
                          }
                        }}
                      >
                        <GooglePlacesAutocomplete
                          label={translate("companies.billing_address")}
                        />
                      </Box>
                      <CustomButton
                        type="submit"
                        variant="primary"
                        text={translate("companies.save_and_continue")}
                        submitting={isLoading}
                        disabled={isLoading}
                        endIcon={<div />}
                        sx={{
                          width: "100%",
                          height: "48px",
                          marginTop: "20px",
                          padding: "0px 16px",
                          fontSize: "15px",
                          backgroundColor: "rgba(184, 114, 42, 1)",
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: "rgba(184, 114, 42, 1)"
                          },
                          "&:disabled": {
                            backgroundColor: "rgba(184, 114, 42, 1)",
                            color: "#fff",
                            svg: {
                              fill: muiColors.text.secondary,
                              fillOpacity: 1,
                              path: {
                                fill: muiColors.text.secondary,
                                fillOpacity: 1
                              }
                            }
                          }
                        }}
                      />
                    </SimpleForm>
                  )}
                  {selectedPlan === "BASIC" && (
                    <div className="p-4">
                      <CustomButton
                        type="button"
                        onClick={submit}
                        variant="primary"
                        text={translate("companies.save_and_continue")}
                        submitting={isLoading}
                        disabled={isLoading}
                        endIcon={<div />}
                        sx={{
                          width: "100%",
                          height: "48px",
                          marginTop: "20px",
                          padding: "0px 16px",
                          fontSize: "15px",
                          backgroundColor: "rgba(184, 114, 42, 1)",
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: "rgba(184, 114, 42, 1)"
                          },
                          "&:disabled": {
                            backgroundColor: "rgba(184, 114, 42, 1)",
                            color: "#fff",
                            svg: {
                              fill: muiColors.text.secondary,
                              fillOpacity: 1,
                              path: {
                                fill: muiColors.text.secondary,
                                fillOpacity: 1
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  )}
                </Box>
              </div>
            </div>
          )}
          <div className="w-full flex justify-center mt-0 md:mt-6">
            <div className="max-w-[630px] px-0 sm:px-6 text-mono-primaryTextColor text-[16px] md:text-[20px] text-left sm:text-center mt-[-3px] mb-6">
              {translate(
                "companies.curious_how_each_plan_works_schedule_a_live"
              )}{" "}
              <a
                href="https://calendly.com/jennifer-pointmentapp/30min"
                target="_blank"
                rel="noreferrer"
                className="text-mono-primary text-[16px] md:text-[20px] font-['Inter'] font-[400]"
              >
                {translate("companies.demo")}
              </a>{" "}
              {translate("companies.or_click")}{" "}
              <a
                href="https://business.pointmentapp.com"
                target="_blank"
                rel="noreferrer"
                className="text-mono-primary text-[16px] md:text-[20px] font-['Inter'] font-[400]"
              >
                {translate("companies.here")}
              </a>{" "}
              {translate("companies.to_learn_more")}
              <span className="text-mono-primaryTextColor">.</span>
            </div>
          </div>
          {onClose && (
            <div className="flex min-[968px]:hidden w-full justify-center items-center">
              <div>
                <CustomButton
                  variant="secondary"
                  text={translate("common.cancel")}
                  onClick={onClose}
                  sx={{
                    height: "40px",
                    marginTop: "0px",
                    marginBottom: "24px",
                    padding: "0px 24px",
                    fontSize: "15px"
                  }}
                />
              </div>
            </div>
          )}
        </div>
        {selectedPlanToConfirm && (
          <ConfirmationModal
            open={Boolean(selectedPlanToConfirm)}
            handleConfirm={() => {
              setIsLoading(true);
              submit({});
            }}
            onClose={() => setSelectedPlanToConfirm(null)}
            // text={`Are you sure you want to change plan from ${alreadySelectedPlan} to ${selectedPlanToConfirm}? Changing plan will reset your current plan's billing cycle.`}
            text={translate(
              changePlanWarningMessages[
                `${alreadySelectedPlan}-${selectedPlanToConfirm}-${
                  hasAnyFutureBooking ? "WITH" : "WITHOUT"
                }` as keyof typeof changePlanWarningMessages
              ]
            )?.replaceAll(
              "[DATE]",
              new Date(
                currentPeriodEndAt || cancelAtPeriodEndAt
              ).toLocaleDateString()
            )}
            buttonText="Confirm"
            disabled={isLoading}
            submitting={isLoading}
            title=""
            buttonBg={muiColors.primary}
            centerAlign
          />
        )}
        {/* </div> */}
      </DialogContent>
    </Dialog>
  );
}

const validationSchema = yup
  .object()
  .shape({
    address: yup.string().nullable().required("Address is required"),
    city: yup.string().nullable().required("City is required"),
    state: yup.string().nullable().required("State is required"),
    country: yup.string().nullable().required("Country is required"),
    zipcode: yup.string().nullable().required("Zipcode is required")
  })
  .required();
